import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  BarChart3, DollarSign, RefreshCw, CreditCard, 
  Plus, Edit, Wallet, CreditCard as CreditIcon, Loader2, X, Search
} from "lucide-react";
import { formatPhone, formatCPF } from '@/utils/formatters';
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';

import { 
  CreditoService, 
  ClienteCredito, 
  CreditoTransacao 
} from '@/services/creditoService';
import { toast } from 'sonner';

// Tipo adicional para dados de reserva embutidos na transação retornada pela API
interface ReservaInfo {
  data: string;
  sala_nome: string;
  horario: string;
}

const AdminCreditos = () => {
  const [clientes, setClientes] = useState<ClienteCredito[]>([]);
  const [clientesAjuste, setClientesAjuste] = useState<ClienteCredito[]>([]);
  const [transacoes, setTransacoes] = useState<CreditoTransacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [ajusteCredito, setAjusteCredito] = useState({
    cliente_id: "",
    valor: "",
    descricao: "",
    tipo: "ADICIONAR" as "ADICIONAR" | "REMOVER"
  });
  const [modalAberto, setModalAberto] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<ClienteCredito | null>(null);
  const [transacaoSelecionada, setTransacaoSelecionada] = useState<CreditoTransacao | null>(null);

  const [activeTab, setActiveTab] = useState<"clientes" | "transacoes-externas" | "ajustes" | "historico-ajustes" | "transacoes-internas">("clientes");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroClienteAjuste, setFiltroClienteAjuste] = useState("");
  const [filtroClienteExternas, setFiltroClienteExternas] = useState("");
  const [filtroClienteInternas, setFiltroClienteInternas] = useState("");
  const [filtroTipoTransacao, setFiltroTipoTransacao] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientesData, clientesAjusteData, transacoesData] = await Promise.all([
        CreditoService.getClientesComCredito(),
        CreditoService.getClientesParaAjuste(),
        CreditoService.getAllTransacoes(1, 1000)
      ]);

      setClientes(clientesData);
      setClientesAjuste(clientesAjusteData);
      setTransacoes(transacoesData.transacoes);
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalAjuste = (cliente: ClienteCredito) => {
    setClienteSelecionado(cliente);
    setAjusteCredito({
      cliente_id: cliente.user_id,
      valor: "",
      descricao: "",
      tipo: "ADICIONAR"
    });
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setClienteSelecionado(null);
    setAjusteCredito({
      cliente_id: "",
      valor: "",
      descricao: "",
      tipo: "ADICIONAR"
    });
  };

  const abrirDetalhes = (t: CreditoTransacao) => setTransacaoSelecionada(t);
  const fecharDetalhes = () => setTransacaoSelecionada(null);

  useEffect(() => {
    loadData();
  }, []);

  // Configuração das colunas para clientes
  // Filtrar e ordenar clientes
  const clientesFiltrados = clientes
    .filter(cliente => 
      (cliente.nome ?? '').toLowerCase().includes(filtroCliente.toLowerCase()) ||
      (cliente.email ?? '').toLowerCase().includes(filtroCliente.toLowerCase()) ||
      (cliente.cpf ?? '').includes(filtroCliente)
    )
    .sort((a, b) => {
      const nomeA = (a.nome ?? '').toString();
      const nomeB = (b.nome ?? '').toString();
      return nomeA < nomeB ? -1 : nomeA > nomeB ? 1 : 0;
    });

  // Filtrar clientes para ajuste
  const clientesAjusteFiltrados = clientesAjuste
    .filter(cliente => 
      (cliente.nome ?? '').toLowerCase().includes(filtroClienteAjuste.toLowerCase()) ||
      (cliente.email ?? '').toLowerCase().includes(filtroClienteAjuste.toLowerCase()) ||
      (cliente.cpf ?? '').includes(filtroClienteAjuste)
    )
    .sort((a, b) => {
      const nomeA = (a.nome ?? '').toString();
      const nomeB = (b.nome ?? '').toString();
      return nomeA < nomeB ? -1 : nomeA > nomeB ? 1 : 0;
    });

  // Filtrar transações externas (COMPRA) ordenadas por data (mais recente primeiro)
  const transacoesExternas = transacoes
    .filter(t => t.tipo === 'COMPRA')
    .filter(t => 
      !filtroClienteExternas || 
      t.user?.nome_completo?.toLowerCase().includes(filtroClienteExternas.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Filtrar transações internas (RESERVA, ESTORNO, USADO) ordenadas por data (mais recente primeiro)
  const transacoesInternas = transacoes
    .filter(t => ['RESERVA', 'ESTORNO', 'USADO'].includes(t.tipo))
    .filter(t => 
      !filtroClienteInternas || 
      t.user?.nome_completo?.toLowerCase().includes(filtroClienteInternas.toLowerCase())
    )
    .filter(t => 
      !filtroTipoTransacao || t.tipo === filtroTipoTransacao
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const { createColumn: createClientColumn } = useTableColumns<ClienteCredito>();
  
  const clientColumns: ColumnDef<ClienteCredito>[] = [
    createClientColumn('nome_completo', 'Cliente', (cliente) => (
      <div>
        <p className="font-medium uppercase">{cliente.nome}</p>
        <p className="text-sm text-gray-500">{cliente.email}</p>
      </div>
    )),
    createClientColumn('telefone', 'Telefone', (cliente) => (
      <span className="text-sm">{formatPhone(cliente.telefone)}</span>
    )),
    createClientColumn('cpf', 'CPF', (cliente) => (
      <span className="text-sm">{formatCPF(cliente.cpf)}</span>
    )),
    createClientColumn('saldo', 'Saldo', (cliente) => (
      <span className="font-semibold text-green-600">
        {formatCurrency(cliente.saldo)}
      </span>
    )),
    createClientColumn('totalTransacoes', 'Total Transações', (cliente) => 
      cliente.totalTransacoes
    )
  ];

  // Colunas para tabela de ajustes (todos os clientes)
  const ajusteColumns: ColumnDef<ClienteCredito>[] = [
    createClientColumn('nome_completo', 'Cliente', (cliente) => (
      <div>
        <p className="font-medium uppercase">{cliente.nome}</p>
        <p className="text-sm text-gray-500">{cliente.email}</p>
      </div>
    )),
    createClientColumn('telefone', 'Telefone', (cliente) => (
      <span className="text-sm">{formatPhone(cliente.telefone)}</span>
    )),
    createClientColumn('cpf', 'CPF', (cliente) => (
      <span className="text-sm">{formatCPF(cliente.cpf)}</span>
    )),
    createClientColumn('saldo', 'Saldo', (cliente) => (
      <span className={`font-semibold ${cliente.saldo > 0 ? 'text-green-600' : 'text-gray-500'}`}>
        {formatCurrency(cliente.saldo)}
      </span>
    )),
    createClientColumn('totalTransacoes', 'Transações', (cliente) => (
      <span className="text-sm text-gray-600">
        {cliente.totalTransacoes}
      </span>
    )),
    createClientColumn('status', 'Status', (cliente) => (
      <Badge 
        className={
          cliente.saldo > 0 
            ? 'bg-green-100 text-green-800' 
            : cliente.totalTransacoes > 0 
              ? 'bg-gray-100 text-gray-800'
              : 'bg-blue-100 text-blue-800'
        }
      >
        {cliente.saldo > 0 
          ? 'Com Saldo' 
          : cliente.totalTransacoes > 0 
            ? 'Sem Saldo'
            : 'Novo Cliente'
        }
      </Badge>
    )),
    createClientColumn('actions', 'Ações', (cliente) => (
      <Button 
        size="sm" 
        variant="outline"
        onClick={() => abrirModalAjuste(cliente)}
      >
        <Edit className="w-4 h-4 mr-2" />
        Ajustar Saldo
      </Button>
    ))
  ];

  // Configuração das colunas para transações externas
  const { createColumn: createTransactionColumn } = useTableColumns<CreditoTransacao>();
  
  const externalTransactionColumns: ColumnDef<CreditoTransacao>[] = [
    createTransactionColumn('tipo', 'Tipo', (transacao) => (
      <div className="flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-green-600" />
        <Badge className="bg-green-100 text-green-800">
          Compra de Crédito
        </Badge>
      </div>
    )),
    createTransactionColumn('cliente', 'Cliente', (transacao) => 
      transacao.user?.nome_completo || 'N/A'
    ),
    createTransactionColumn('valor', 'Valor Creditado', (transacao) => (
      <span className="text-green-600 font-semibold">
        {formatCurrency(Math.abs(transacao.valor))}
      </span>
    )),
    createTransactionColumn('created_at', 'Data da Operação', (transacao) => {
      const data = new Date(transacao.created_at);
      return (
        <div>
          <p className="font-medium">{data.toLocaleDateString('pt-BR')}</p>
          <p className="text-sm text-gray-600">{data.toLocaleTimeString('pt-BR')}</p>
        </div>
      );
    }),
    createTransactionColumn('acao', 'Ação', (transacao) => (
      <Button size="sm" variant="outline" onClick={() => abrirDetalhes(transacao)}>
        Detalhes
      </Button>
    ))
  ];

  // Configuração das colunas para transações internas
  const internalTransactionColumns: ColumnDef<CreditoTransacao>[] = [
    createTransactionColumn('tipo', 'Tipo', (transacao) => (
      <div className="flex items-center gap-2">
        {getTipoIcon(transacao.tipo)}
        <Badge className={getTipoColor(transacao.tipo)}>
          {getTipoLabel(transacao.tipo)}
        </Badge>
      </div>
    )),
    createTransactionColumn('cliente', 'Cliente', (transacao) => 
      transacao.user?.nome_completo || 'N/A'
    ),
    createTransactionColumn('valor', 'Valor', (transacao) => (
      <span className={transacao.valor > 0 ? 'text-green-600' : 'text-red-600'}>
        {formatCurrency(Math.abs(transacao.valor))}
      </span>
    )),
    createTransactionColumn('created_at', 'Data da Operação', (transacao) => {
      const data = new Date(transacao.created_at);
      return (
        <div>
          <p className="font-medium">{data.toLocaleDateString('pt-BR')}</p>
          <p className="text-sm text-gray-600">{data.toLocaleTimeString('pt-BR')}</p>
        </div>
      );
    }),
    createTransactionColumn('acao', 'Ação', (transacao) => (
      <Button size="sm" variant="outline" onClick={() => abrirDetalhes(transacao)}>
        Detalhes
      </Button>
    ))
  ];

  // Configuração das colunas para transações gerais (visão geral)
  const transactionColumns: ColumnDef<CreditoTransacao>[] = [
    createTransactionColumn('tipo', 'Tipo', (transacao) => (
      <div className="flex items-center gap-2">
        {getTipoIcon(transacao.tipo)}
        <Badge className={getTipoColor(transacao.tipo)}>
          {getTipoLabel(transacao.tipo)}
        </Badge>
      </div>
    )),
    createTransactionColumn('cliente', 'Cliente', (transacao) => 
      transacao.user?.nome_completo || 'N/A'
    ),
    createTransactionColumn('valor', 'Valor', (transacao) => (
      <span className={transacao.valor > 0 ? 'text-green-600' : 'text-red-600'}>
        {formatCurrency(Math.abs(transacao.valor))}
      </span>
    )),
    createTransactionColumn('descricao', 'Descrição', (transacao) => {
      let descricao = transacao.observacao || 'Sem descrição';
      
      // Adicionar informações da reserva se disponível
      const reservaInfo = (transacao as CreditoTransacao & { reserva_info?: ReservaInfo }).reserva_info;
      if (reservaInfo) {
        const dataFormatada = new Date(reservaInfo.data).toLocaleDateString('pt-BR');
        descricao += ` - ${reservaInfo.sala_nome} em ${dataFormatada} (${reservaInfo.horario})`;
      }
      
      return (
        <div>
          <p className="text-sm">{descricao}</p>
        </div>
      );
    }),
    createTransactionColumn('created_at', 'Data', (transacao) => 
      new Date(transacao.created_at).toLocaleDateString('pt-BR')
    )
  ];

  const handleAjusteCredito = async () => {
    if (ajusteCredito.cliente_id && ajusteCredito.valor && ajusteCredito.descricao) {
      try {
        await CreditoService.ajustarCredito({
          user_id: ajusteCredito.cliente_id,
          valor: parseFloat(ajusteCredito.valor),
          observacao: ajusteCredito.descricao,
          tipo: ajusteCredito.tipo
        });

        toast.success('Crédito ajustado com sucesso!');
        fecharModal();
        
        // Recarregar todos os dados (incluindo clientes para ajuste)
        await loadData();
      } catch (err: unknown) {
        const error = err as { message?: string };
        toast.error(error?.message || 'Erro ao ajustar crédito');
      }
    } else {
      toast.error('Preencha todos os campos obrigatórios');
    }
  };

  const formatCurrency = (value: number) => {
    // Verificar se o valor é válido para evitar NaN
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'COMPRA':
        return <Plus className="w-4 h-4 text-green-600" />;
      case 'RESERVA':
        return <CreditIcon className="w-4 h-4 text-orange-600" />;
      case 'USADO':
        return <BarChart3 className="w-4 h-4 text-purple-600" />;
      case 'ESTORNO':
        return <RefreshCw className="w-4 h-4 text-blue-600" />;
      case 'AJUSTE':
        return <Edit className="w-4 h-4 text-gray-600" />;
      case 'REEMBOLSO':
        return <DollarSign className="w-4 h-4 text-red-600" />;
      default:
        return <Wallet className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'COMPRA':
        return 'Compra';
      case 'RESERVA':
        return 'Reserva';
      case 'USADO':
        return 'Usado';
      case 'ESTORNO':
        return 'Estorno';
      case 'AJUSTE':
        return 'Ajuste';
      case 'REEMBOLSO':
        return 'Reembolso';
      default:
        return tipo;
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'COMPRA':
        return 'bg-green-100 text-green-800';
      case 'RESERVA':
        return 'bg-orange-100 text-orange-800';
      case 'USADO':
        return 'bg-purple-100 text-purple-800';
      case 'ESTORNO':
        return 'bg-blue-100 text-blue-800';
      case 'AJUSTE':
        return 'bg-gray-100 text-gray-800';
      case 'REEMBOLSO':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <ResponsiveAdminLayout
        title="Gestão de Créditos"
        subtitle="Controle de créditos e transações dos clientes"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Carregando dados...</p>
          </div>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveAdminLayout
      title="Gestão de Créditos"
      subtitle="Controle de créditos e transações dos clientes"
    >
      {/* Navigation Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-1 bg-gray-200 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab("clientes")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "clientes"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Saldo dos Clientes
          </button>
          <button
            onClick={() => setActiveTab("ajustes")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "ajustes"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Pagamentos Via PIX
          </button>
          <button
            onClick={() => setActiveTab("transacoes-externas")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "transacoes-externas"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Histórico de Compras pelo Stripe
          </button>
          <button
            onClick={() => setActiveTab("historico-ajustes")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "historico-ajustes"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Histórico de Compras por PIX
          </button>
          <button
            onClick={() => setActiveTab("transacoes-internas")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "transacoes-internas"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Histórico de Transações Internas
          </button>
        </div>
      </div>



      {/* Clientes Tab */}
      {activeTab === "clientes" && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold">Saldo dos Clientes</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por nome, email ou CPF..."
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                    className="w-full sm:w-80 pl-10"
                  />
                </div>
                {filtroCliente && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltroCliente("")}
                    title="Limpar filtro"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {filtroCliente && (
              <p className="text-sm text-gray-600 mt-2">
                Mostrando {clientesFiltrados.length} de {clientes.length} clientes
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              data={clientesFiltrados}
              columns={clientColumns}
              emptyMessage={filtroCliente ? "Nenhum cliente encontrado com este filtro" : "Nenhum cliente encontrado"}
            />
          </CardContent>
        </Card>
      )}

      {/* Transações Externas Tab */}
      {activeTab === "transacoes-externas" && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold">Histórico de Compras pelo Stripe</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por cliente..."
                    value={filtroClienteExternas}
                    onChange={(e) => setFiltroClienteExternas(e.target.value)}
                    className="w-full sm:w-64 pl-10"
                  />
                </div>
                {filtroClienteExternas && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltroClienteExternas("")}
                    title="Limpar filtro"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {filtroClienteExternas && (
              <p className="text-sm text-gray-600 mt-2">
                Mostrando {transacoesExternas.length} transações
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              data={transacoesExternas}
              columns={externalTransactionColumns}
              emptyMessage={filtroClienteExternas ? "Nenhuma transação externa encontrada com este filtro" : "Nenhuma transação externa encontrada"}
            />
          </CardContent>
        </Card>
      )}

      {/* Transações Internas Tab */}
      {activeTab === "transacoes-internas" && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg font-semibold">Histórico de Transações Internas</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por cliente..."
                    value={filtroClienteInternas}
                    onChange={(e) => setFiltroClienteInternas(e.target.value)}
                    className="w-full sm:w-48 pl-10"
                  />
                </div>
                <select
                  value={filtroTipoTransacao}
                  onChange={(e) => setFiltroTipoTransacao(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Todos os tipos</option>
                  <option value="RESERVA">Reserva</option>
                  <option value="ESTORNO">Estorno</option>
                  <option value="USADO">Usado</option>
                </select>
                {(filtroClienteInternas || filtroTipoTransacao) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFiltroClienteInternas("");
                      setFiltroTipoTransacao("");
                    }}
                    title="Limpar filtros"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {(filtroClienteInternas || filtroTipoTransacao) && (
              <p className="text-sm text-gray-600 mt-2">
                Mostrando {transacoesInternas.length} transações
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              data={transacoesInternas}
              columns={internalTransactionColumns}
              emptyMessage={
                (filtroClienteInternas || filtroTipoTransacao) 
                  ? "Nenhuma transação interna encontrada com estes filtros" 
                  : "Nenhuma transação interna encontrada"
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Pagamentos Via PIX Tab */}
      {activeTab === "ajustes" && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg font-semibold">Adicionar Créditos Pagos Via PIX</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Todos os clientes ativos - incluindo aqueles que nunca compraram créditos
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por nome, email ou CPF..."
                    value={filtroClienteAjuste}
                    onChange={(e) => setFiltroClienteAjuste(e.target.value)}
                    className="w-full sm:w-80 pl-10"
                  />
                </div>
                {filtroClienteAjuste && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFiltroClienteAjuste("")}
                    title="Limpar filtro"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            {filtroClienteAjuste && (
              <p className="text-sm text-gray-600 mt-2">
                Mostrando {clientesAjusteFiltrados.length} de {clientesAjuste.length} clientes
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ResponsiveTable
              data={clientesAjusteFiltrados}
              columns={ajusteColumns}
              emptyMessage={filtroClienteAjuste ? "Nenhum cliente encontrado com este filtro" : "Nenhum cliente encontrado"}
              onRowClick={(cliente) => abrirModalAjuste(cliente)}
            />
          </CardContent>
        </Card>
      )}

      {/* Histórico de Compras por PIX Tab */}
      {activeTab === "historico-ajustes" && (
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Histórico de Compras por PIX</CardTitle>
            <p className="text-sm text-gray-600 mt-1">
              Ajustes manuais de saldo realizados pelo administrador (pagamentos via PIX)
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transacoes
                .filter(t => t.tipo === 'AJUSTE')
                .map((ajuste) => (
                  <div key={ajuste.id} className="flex items-center justify-between p-4 border rounded-lg bg-purple-50">
                    <div className="flex items-center gap-3">
                      <Edit className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-gray-900">{ajuste.user.nome_completo}</p>
                        <p className="text-sm text-gray-600">{ajuste.observacao || 'Sem observação'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(ajuste.created_at).toLocaleDateString('pt-BR')} às {new Date(ajuste.created_at).toLocaleTimeString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${ajuste.valor >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ajuste.valor >= 0 ? '+' : ''}{formatCurrency(ajuste.valor)}
                      </p>
                      <Badge className="bg-purple-100 text-purple-800">Pagamento PIX</Badge>
                    </div>
                  </div>
                ))}
              {transacoes.filter(t => t.tipo === 'AJUSTE').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Edit className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum pagamento via PIX registrado ainda</p>
                  <p className="text-sm mt-2">Use a aba 'Pagamentos Via PIX' para registrar pagamentos</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}



      {/* Modal de Ajuste de Créditos */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Ajustar Créditos
            </DialogTitle>
            <DialogDescription>
              {clienteSelecionado && (
                <span>
                  Ajustar créditos para <strong>{clienteSelecionado.nome}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {clienteSelecionado && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{clienteSelecionado.nome}</p>
                    <p className="text-sm text-gray-600">{clienteSelecionado.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Saldo Atual</p>
                    <p className="font-semibold text-green-600">
                      {formatCurrency(clienteSelecionado.saldo)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="tipo-ajuste-modal">Tipo de Ajuste</Label>
                <select
                  id="tipo-ajuste-modal"
                  value={ajusteCredito.tipo}
                  onChange={(e) => setAjusteCredito({...ajusteCredito, tipo: e.target.value as "ADICIONAR" | "REMOVER"})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-12"
                >
                  <option value="ADICIONAR">Adicionar Crédito</option>
                  <option value="REMOVER">Remover Crédito</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="valor-ajuste-modal">Valor</Label>
                <Input
                  id="valor-ajuste-modal"
                  type="number"
                  step="0.01"
                  value={ajusteCredito.valor}
                  onChange={(e) => setAjusteCredito({...ajusteCredito, valor: e.target.value})}
                  placeholder="0,00"
                  className="h-12"
                />
              </div>
              
              <div>
                <Label htmlFor="descricao-ajuste-modal">Descrição</Label>
                <Input
                  id="descricao-ajuste-modal"
                  value={ajusteCredito.descricao}
                  onChange={(e) => setAjusteCredito({...ajusteCredito, descricao: e.target.value})}
                  placeholder="Motivo do ajuste"
                  className="h-12"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={fecharModal}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button 
                onClick={handleAjusteCredito} 
                className="flex-1"
                disabled={!ajusteCredito.valor || !ajusteCredito.descricao}
              >
                <Edit className="w-4 h-4 mr-2" />
                Aplicar Ajuste
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes Transação */}
      <Dialog open={!!transacaoSelecionada} onOpenChange={fecharDetalhes}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Operação</DialogTitle>
          </DialogHeader>
          {transacaoSelecionada && (
            <div className="space-y-2">
              <p>
                <span className="font-medium">Tipo:</span> {getTipoLabel(transacaoSelecionada.tipo)}
              </p>
              <p>
                <span className="font-medium">Cliente:</span> {transacaoSelecionada.user?.nome_completo || 'N/A'}
              </p>
              <p>
                <span className="font-medium">Valor:</span> {formatCurrency(Math.abs(transacaoSelecionada.valor))}
              </p>
              <p>
                <span className="font-medium">Data:</span> {new Date(transacaoSelecionada.created_at).toLocaleString('pt-BR')}
              </p>
              {transacaoSelecionada.observacao && (
                <p className="italic">"{transacaoSelecionada.observacao}"</p>
              )}
              {transacaoSelecionada.tipo === 'COMPRA' && (
                <div className="space-y-1 text-sm text-gray-700 border-t pt-2">
                  <p className="font-medium">Compra via Stripe</p>
                  {transacaoSelecionada.pagamento_id && (
                    <p><span className="font-medium">ID Pagamento:</span> {transacaoSelecionada.pagamento_id}</p>
                  )}
                </div>
              )}
              {((transacaoSelecionada as CreditoTransacao & { reserva_info?: ReservaInfo }).reserva_info) && (
                <div className="border-t pt-2 space-y-1 text-sm text-gray-700">
                  <p className="font-medium">Detalhes da Reserva:</p>
                  {(() => {
                    const reserva = (transacaoSelecionada as CreditoTransacao & { reserva_info?: ReservaInfo }).reserva_info as ReservaInfo;
                    return (
                      <>
                        <p>Sala: {reserva.sala_nome}</p>
                        <p>Data: {new Date(reserva.data).toLocaleDateString('pt-BR')}</p>
                        <p>Horário: {reserva.horario}</p>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={fecharDetalhes}>Fechar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ResponsiveAdminLayout>
  );
};

export default AdminCreditos; 
